import { SPLIT_LAYOUTS, type ContentSlide } from './types';

export interface ZoneImageRef {
  imageKey: string | undefined;
  /** JSON path to the imageKey field, e.g. `zones.image.imageKey`. */
  imagePath: string;
}

/** True when the layout reserves a media column, stacked band, or backdrop for a zone image. */
export function slideHasMediaLayout(slide: ContentSlide): boolean {
  if (slide.layout === 'title_slide' || slide.layout === 'text_and_background') return true;
  /*
    Speech bubbles are a flip_cards VARIANT, and flip_cards is not a split layout — but this one
    variant draws a full-bleed photo behind the bubble row (Figma "Flip cards / Speech bubble"), and
    `isBackgroundImagePath` below already treats `zones.image.imageKey` as that backdrop's slot. Left
    out of here, the two disagreed: the backdrop had no document path, so `SlideImage` got no
    `editImagePath` and the Change image button was missing from this variant alone — and with no key
    set yet there was no placeholder to aim at either.
  */
  if (slide.layout === 'flip_cards' && slide.variant === 'speech_bubble') return true;
  return SPLIT_LAYOUTS.has(slide.layout);
}

/** Maps an imageKey document path to its paired imageAlt path when one exists. */
export function imageAltPathFromImageKeyPath(imagePath: string): string | null {
  if (imagePath.endsWith('.imageKey')) {
    return imagePath.replace(/\.imageKey$/, '.imageAlt');
  }
  return null;
}

function readNestedString(slide: ContentSlide, path: string): string | undefined {
  const segments = path.split('.');
  let current: unknown = slide;
  for (const segment of segments) {
    const arrayMatch = segment.match(/^(\w+)\[(\d+)\]$/);
    if (arrayMatch) {
      const [, key, indexText] = arrayMatch;
      if (!current || typeof current !== 'object') return undefined;
      const items = (current as Record<string, unknown>)[key];
      if (!Array.isArray(items)) return undefined;
      current = items[Number(indexText)];
      continue;
    }
    if (!current || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[segment];
  }
  return typeof current === 'string' && current.trim().length > 0 ? current : undefined;
}

/** Matches registry background slots used by the authoring image pipeline. */
function isBackgroundImagePath(slide: ContentSlide, imagePath: string): boolean {
  if (imagePath === 'zones.image.imageKey') {
    if (slide.layout === 'title_slide' || slide.layout === 'text_and_background') {
      return true;
    }
    if (slide.layout === 'flip_cards' && slide.variant === 'speech_bubble') {
      return true;
    }
  }

  if (/^interaction\.items\[\d+\]\.imageKey$/.test(imagePath) && slide.layout === 'accordion_horizontal') {
    return true;
  }

  return false;
}

/** Reads existing alt text for a slide image path, when the model stores imageAlt beside imageKey. */
export function readSlideImageAltAtPath(slide: ContentSlide, imagePath: string): string | undefined {
  const altPath = imageAltPathFromImageKeyPath(imagePath);
  if (!altPath) return undefined;
  return readNestedString(slide, altPath);
}

/** Default alt text when the author assigns an image but has not supplied alt copy yet. */
export function buildDefaultSlideImageAlt(slide: ContentSlide, imagePath: string): string {
  if (
    imagePath === 'interaction.imageKey' &&
    slide.interaction &&
    typeof slide.interaction === 'object' &&
    'items' in slide.interaction &&
    Array.isArray(slide.interaction.items)
  ) {
    const count = slide.interaction.items.length;
    if (count > 0) {
      return `Image showing ${count} interactive item${count === 1 ? '' : 's'} on this slide`;
    }
  }

  const itemMatch = imagePath.match(/^interaction\.items\[(\d+)\]\.imageKey$/);
  if (itemMatch && slide.interaction && 'items' in slide.interaction) {
    const itemIndex = Number(itemMatch[1]);
    const item = slide.interaction.items?.[itemIndex];
    if (item && typeof item === 'object') {
      const label =
        ('label' in item && typeof item.label === 'string' ? item.label : undefined) ??
        ('heading' in item && typeof item.heading === 'string' ? item.heading : undefined);
      const trimmed = label?.trim();
      if (trimmed) {
        return `Illustration representing ${trimmed}`;
      }
    }
  }

  const subject = slide.title?.trim();
  if (isBackgroundImagePath(slide, imagePath)) {
    return subject ? `Decorative background image for ${subject}` : 'Decorative background image';
  }
  if (subject) {
    return `Illustration representing ${subject}`;
  }
  return 'Illustration for this slide';
}

/**
 * Locates where a slide's zone image key lives in the document.
 * Returns a default path for media layouts even when no key is set yet.
 */
export function resolveZoneImageRef(slide: ContentSlide): ZoneImageRef | null {
  if (!slideHasMediaLayout(slide)) return null;

  // Hotspot in column renders its image inside HotspotPayload — not in the frame media column.
  if (slide.layout === 'hotspot_in_column') {
    return null;
  }

  if (slide.zones?.image?.imageKey !== undefined) {
    return { imageKey: slide.zones.image.imageKey, imagePath: 'zones.image.imageKey' };
  }
  if (slide.zones?.heading?.imageKey) {
    return { imageKey: slide.zones.heading.imageKey, imagePath: 'zones.heading.imageKey' };
  }
  if (slide.zones?.body?.imageKey) {
    return { imageKey: slide.zones.body.imageKey, imagePath: 'zones.body.imageKey' };
  }
  if (slide.source?.images?.[0]) {
    return { imageKey: slide.source.images[0], imagePath: 'source.images[0]' };
  }

  if (slide.layout === 'title_slide') {
    return { imageKey: undefined, imagePath: 'zones.image.imageKey' };
  }

  return { imageKey: undefined, imagePath: 'zones.image.imageKey' };
}

function setSlideImageAltAtPath(slide: ContentSlide, altPath: string, imageAlt: string): ContentSlide {
  switch (altPath) {
    case 'zones.image.imageAlt':
      return {
        ...slide,
        zones: {
          ...slide.zones,
          image: { ...slide.zones?.image, imageAlt },
        },
      };
    case 'zones.heading.imageAlt':
      return {
        ...slide,
        zones: {
          ...slide.zones,
          heading: { ...slide.zones?.heading, imageAlt },
        },
      };
    case 'zones.body.imageAlt':
      return {
        ...slide,
        zones: {
          ...slide.zones,
          body: { ...slide.zones?.body, imageAlt },
        },
      };
    case 'interaction.imageAlt': {
      if (!slide.interaction) return slide;
      return {
        ...slide,
        interaction: {
          ...slide.interaction,
          imageAlt,
        } as typeof slide.interaction,
      };
    }
    default: {
      const zoneAltMatch = altPath.match(/^zones\.([^.]+)\.imageAlt$/);
      if (zoneAltMatch) {
        const zoneKey = zoneAltMatch[1];
        return {
          ...slide,
          zones: {
            ...slide.zones,
            [zoneKey]: { ...slide.zones?.[zoneKey], imageAlt },
          },
        };
      }

      const itemPathMatch = altPath.match(/^interaction\.items\[(\d+)\]\.imageAlt$/);
      if (!itemPathMatch || !slide.interaction || !('items' in slide.interaction)) {
        return slide;
      }
      const itemIndex = Number(itemPathMatch[1]);
      const items = [...(slide.interaction.items ?? [])];
      const item = items[itemIndex];
      if (!item || typeof item !== 'object') return slide;
      items[itemIndex] = { ...item, imageAlt };
      return {
        ...slide,
        interaction: {
          ...slide.interaction,
          items,
        } as typeof slide.interaction,
      };
    }
  }
}

/** Immutable update of an image key (and optional alt text) at a slide document path. */
export function setSlideImageAtPath(
  slide: ContentSlide,
  imagePath: string,
  imageKey: string,
  imageAlt?: string,
): ContentSlide {
  let next: ContentSlide;
  switch (imagePath) {
    case 'zones.image.imageKey':
      next = {
        ...slide,
        zones: {
          ...slide.zones,
          image: { ...slide.zones?.image, imageKey },
        },
      };
      break;
    case 'zones.heading.imageKey':
      next = {
        ...slide,
        zones: {
          ...slide.zones,
          heading: { ...slide.zones?.heading, imageKey },
        },
      };
      break;
    case 'zones.body.imageKey':
      next = {
        ...slide,
        zones: {
          ...slide.zones,
          body: { ...slide.zones?.body, imageKey },
        },
      };
      break;
    case 'source.images[0]': {
      const images = [...(slide.source?.images ?? [])];
      images[0] = imageKey;
      next = {
        ...slide,
        source: { ...slide.source, images },
      };
      break;
    }
    case 'interaction.imageKey': {
      if (!slide.interaction) return slide;
      next = {
        ...slide,
        interaction: {
          ...slide.interaction,
          imageKey,
        } as typeof slide.interaction,
      };
      break;
    }
    default: {
      const zoneKeyMatch = imagePath.match(/^zones\.([^.]+)\.imageKey$/);
      if (zoneKeyMatch) {
        const zoneKey = zoneKeyMatch[1];
        next = {
          ...slide,
          zones: {
            ...slide.zones,
            [zoneKey]: { ...slide.zones?.[zoneKey], imageKey },
          },
        };
        break;
      }

      const itemPathMatch = imagePath.match(/^interaction\.items\[(\d+)\]\.imageKey$/);
      if (!itemPathMatch || !slide.interaction || !('items' in slide.interaction)) {
        return slide;
      }
      const itemIndex = Number(itemPathMatch[1]);
      const items = [...(slide.interaction.items ?? [])];
      const item = items[itemIndex];
      if (!item || typeof item !== 'object') return slide;
      items[itemIndex] = { ...item, imageKey };
      next = {
        ...slide,
        interaction: {
          ...slide.interaction,
          items,
        } as typeof slide.interaction,
      };
      break;
    }
  }

  const altPath = imageAltPathFromImageKeyPath(imagePath);
  if (altPath && imageAlt !== undefined) {
    return setSlideImageAltAtPath(next, altPath, imageAlt);
  }
  return next;
}

/**
 * Fills missing imageAlt beside every imageKey in a slide before persistence.
 * Mirrors the authoring API repair so saves pass slide-model validation.
 */
export function repairMissingImageAltInSlide(slide: ContentSlide): ContentSlide {
  let next = slide;

  for (const [zoneKey, zone] of Object.entries(next.zones ?? {})) {
    const key = zone?.imageKey;
    if (key && !zone?.imageAlt) {
      next = setSlideImageAtPath(
        next,
        `zones.${zoneKey}.imageKey`,
        key,
        buildDefaultSlideImageAlt(next, `zones.${zoneKey}.imageKey`),
      );
    }
  }

  const interaction = next.interaction;
  if (interaction && 'imageKey' in interaction && interaction.imageKey && !interaction.imageAlt) {
    next = setSlideImageAtPath(
      next,
      'interaction.imageKey',
      interaction.imageKey,
      buildDefaultSlideImageAlt(next, 'interaction.imageKey'),
    );
  }

  if (interaction && 'items' in interaction && Array.isArray(interaction.items)) {
    interaction.items.forEach((item, index) => {
      const imageKey = (item as { imageKey?: string }).imageKey;
      const imageAlt = (item as { imageAlt?: string }).imageAlt;
      if (imageKey && !imageAlt) {
        next = setSlideImageAtPath(
          next,
          `interaction.items[${index}].imageKey`,
          imageKey,
          buildDefaultSlideImageAlt(next, `interaction.items[${index}].imageKey`),
        );
      }
    });
  }

  return next;
}

export function repairMissingImageAltInSlides(slides: ContentSlide[]): ContentSlide[] {
  return slides.map(repairMissingImageAltInSlide);
}
